import { _js } from '@ifixit/utils/localize';
import { useEffect, useState } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { FaIcon } from '@ifixit/ui/components/icon';
import { Input } from '@ifixit/ui/components/input';
import { Label } from '@ifixit/ui/components/label';
import { Popover, PopoverContent, PopoverTrigger } from '@ifixit/ui/components/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ifixit/ui/components/tooltip';
import { cn } from '@ifixit/ui/lib/utils';
import { tiptapButtonClassName, tiptapToolbarPointerDown } from './TiptapButton';
import { useEditorContext, useEditorSelector } from '../editor_context';

const VideoInsertForm = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
   const [url, setUrl] = useState('');
   const [caption, setCaption] = useState('');
   const { editor } = useEditorContext();

   useEffect(() => {
      if (isOpen) {
         // eslint-disable-next-line react-hooks/set-state-in-effect
         setUrl('');
         setCaption('');
      }
   }, [isOpen]);

   const onSubmit = () => {
      editor.chain().focus().insertVideoContent(url, caption).run();
      onClose();
   };

   const onCancel = () => {
      editor.commands.focus();
      onClose();
   };

   const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') {
         return;
      }
      event.preventDefault();
      if (url) {
         onSubmit();
      }
   };

   const handleUrlChange = (event: ChangeEvent<HTMLInputElement>) => {
      setUrl(event.currentTarget.value);
   };

   const handleCaptionChange = (event: ChangeEvent<HTMLInputElement>) => {
      setCaption(event.currentTarget.value);
   };

   return (
      <div className="flex flex-col gap-2">
         <div className="flex flex-col gap-1">
            <Label htmlFor="video-url">{_js('YouTube or Vimeo Video URL')}</Label>
            <Input
               id="video-url"
               value={url}
               onChange={handleUrlChange}
               onKeyDown={handleKeyDown}
               className="text-sm"
            />
         </div>
         <div className="flex flex-col gap-1">
            <Label htmlFor="video-caption">{_js('Video Caption (optional)')}</Label>
            <Input
               id="video-caption"
               value={caption}
               onChange={handleCaptionChange}
               onKeyDown={handleKeyDown}
               className="text-sm"
            />
         </div>
         <div className="mt-2 flex justify-end gap-2">
            <button type="button" onClick={onCancel} className="button-secondary button-md">
               {_js('Cancel')}
            </button>
            <button
               type="button"
               onClick={onSubmit}
               disabled={!url}
               className="button-primary button-md"
            >
               {_js('Insert Video')}
            </button>
         </div>
      </div>
   );
};

export const VideoInsertDropdown = () => {
   const [isOpen, setIsOpen] = useState(false);
   const state = useEditorSelector(editor => ({ isDisabled: !editor.state.selection.empty }));

   if (!state) {
      return null;
   }

   const { isDisabled } = state;
   const label = _js('Insert a YouTube or Vimeo Video');

   return (
      <Tooltip>
         <Popover open={isOpen} onOpenChange={setIsOpen}>
            <TooltipTrigger asChild>
               <PopoverTrigger asChild>
                  <button
                     type="button"
                     aria-label={label}
                     disabled={isDisabled}
                     data-active={isOpen ? 'true' : undefined}
                     className={cn(tiptapButtonClassName)}
                     onPointerDown={tiptapToolbarPointerDown}
                  >
                     <FaIcon name="video" />
                  </button>
               </PopoverTrigger>
            </TooltipTrigger>
            <PopoverContent
               side="bottom"
               align="center"
               onCloseAutoFocus={e => e.preventDefault()}
               className="w-auto min-w-80 border-gray-300 bg-gray-50 p-2.5"
            >
               <VideoInsertForm isOpen={isOpen} onClose={() => setIsOpen(false)} />
            </PopoverContent>
         </Popover>
         <TooltipContent side="top" align="start">
            {label}
         </TooltipContent>
      </Tooltip>
   );
};
